import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import LandingNav from '@/pages/home/components/LandingNav';
import { useProducts, type Product } from '@/hooks/useProducts';
import { useAuthContext } from '@/contexts/AuthContext';
import { useCheckout } from '@/hooks/useCheckout';
import { usePaymentProviders } from '@/hooks/usePaymentProviders';
import { supabase } from '@/lib/supabase';

const FALLBACK_CATEGORIES = ['All', 'Payments', 'Identity', 'Crypto', 'Messaging', 'Data', 'AI/ML'];

const planDuration: Record<string, string> = {
  price_1_2_year: '1–2 Year (+2yr)',
  price_3_5_year: '3–5 Year (+5yr)',
  price_lifetime: 'Lifetime (Forever)',
};

type PlanKey = 'price_1_2_year' | 'price_3_5_year' | 'price_lifetime';

function slugify(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export default function MarketplacePage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthContext();
  const { products, loading } = useProducts();
  const { createCheckout, loading: checkoutLoading } = useCheckout();
  const { providers } = usePaymentProviders();

  const [dbCategories, setDbCategories] = useState<string[]>([]);
  const [category, setCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<PlanKey>('price_lifetime');
  const [selectedProvider, setSelectedProvider] = useState<'nowpayments' | 'stripe'>('nowpayments');
  const [checkoutError, setCheckoutError] = useState('');
  const [authBanner, setAuthBanner] = useState(false);

  const categories = dbCategories.length > 0 ? dbCategories : FALLBACK_CATEGORIES;

  // Load categories from DB
  useEffect(() => {
    supabase.from('product_categories').select('name').order('sort_order').then(({ data }) => {
      if (data && data.length > 0) {
        setDbCategories(['All', ...data.map((c: { name: string }) => c.name)]);
      } else {
        setDbCategories(FALLBACK_CATEGORIES);
      }
    });
  }, []);

  // Check which providers are configured
  const nowpaymentsActive = providers.find(p => p.provider === 'nowpayments')?.is_active ?? false;
  const stripeActive = providers.find(p => p.provider === 'stripe')?.is_active ?? false;

  // Default to first available provider
  useEffect(() => {
    if (!nowpaymentsActive && stripeActive) {
      setSelectedProvider('stripe');
    } else if (nowpaymentsActive) {
      setSelectedProvider('nowpayments');
    }
  }, [nowpaymentsActive, stripeActive]);

  const filtered = products.filter(p => {
    const matchCat = category === 'All' || p.category === category;
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase()) || p.description.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch && p.status === 'active';
  });

  const openProduct = (p: Product) => {
    // Navigate to individual product page
    navigate(`/marketplace/${slugify(p.name)}`);
  };

  const openProductModal = (e: React.MouseEvent, p: Product) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      setAuthBanner(true);
      setTimeout(() => setAuthBanner(false), 5000);
      return;
    }
    setSelectedProduct(p);
    setSelectedPlan('price_lifetime');
    setCheckoutError('');
  };

  const handleCheckout = async () => {
    if (!selectedProduct) return;
    setCheckoutError('');

    const result = await createCheckout({
      product_id: selectedProduct.id,
      pricing_type: selectedPlan,
      amount: selectedProduct[selectedPlan],
      provider: selectedProvider,
    });

    if (result.success) {
      const url = result.invoice_url || result.checkout_url;
      if (url) {
        // Redirect to NOWPayments / Stripe payment page
        window.location.href = url;
      } else {
        setCheckoutError('Checkout created but no redirect URL was returned. Check System Logs.');
      }
    } else {
      setCheckoutError(result.error || 'Failed to create checkout. Please try again.');
    }
  };

  const hasAnyProvider = nowpaymentsActive || stripeActive;

  return (
    <div className="min-h-screen bg-gray-50">
      <LandingNav />

      {/* Auth Required Banner */}
      <AnimatePresence>
        {authBanner && (
          <motion.div
            initial={{ opacity: 0, y: -60 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -60 }}
            transition={{ duration: 0.35 }}
            className="fixed top-16 left-0 right-0 z-50 flex justify-center px-4 pt-3"
          >
            <div className="flex items-center gap-4 bg-gray-900 text-white px-5 py-3.5 rounded-2xl shadow-lg max-w-md w-full">
              <div className="w-8 h-8 bg-brand-500/20 rounded-lg flex items-center justify-center shrink-0">
                <i className="ri-lock-line text-brand-400 text-base" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">Sign in required to purchase</p>
                <p className="text-xs text-white/50 mt-0.5">You need an account to buy APIs from the marketplace.</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => navigate('/login?redirect=/marketplace')}
                  className="px-3 py-1.5 bg-brand-500 hover:bg-brand-600 text-white text-xs font-bold rounded-lg cursor-pointer whitespace-nowrap transition-colors"
                >
                  Sign In
                </button>
                <button
                  onClick={() => navigate('/register?redirect=/marketplace')}
                  className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold rounded-lg cursor-pointer whitespace-nowrap transition-colors"
                >
                  Register
                </button>
                <button onClick={() => setAuthBanner(false)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white/10 cursor-pointer ml-1">
                  <i className="ri-close-line text-white/50 text-sm" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="bg-sidebar pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
            <span className="text-xs font-bold text-brand-400 tracking-widest uppercase">API Marketplace</span>
            <h1 className="text-3xl md:text-5xl font-extrabold text-white mt-3">Browse Production APIs</h1>
            <p className="text-white/50 mt-3 max-w-xl mx-auto text-sm">{products.length}+ enterprise-grade APIs. Pay with crypto or card. Get instant access. Lifetime licenses available.</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mt-7 max-w-lg mx-auto">
            <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-4 py-3">
              <i className="ri-search-line text-white/50 text-lg shrink-0" />
              <input
                className="flex-1 bg-transparent text-white placeholder-white/40 text-sm focus:outline-none"
                placeholder="Search APIs by name or category…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="sticky top-16 z-30 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 overflow-x-auto">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={`px-4 py-2 rounded-lg text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors shrink-0 ${category === cat ? 'bg-sidebar text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              {cat}
            </button>
          ))}
          <span className="ml-auto text-xs text-gray-400 shrink-0">{filtered.length} APIs</span>
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-7xl mx-auto px-6 py-10">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5 animate-pulse">
                <div className="h-1.5 w-full bg-gray-200 rounded mb-4" />
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-xl" />
                  <div className="w-16 h-6 bg-gray-200 rounded-full" />
                </div>
                <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
                <div className="h-4 bg-gray-200 rounded w-full mb-4" />
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="h-10 bg-gray-200 rounded-lg" />
                  <div className="h-10 bg-gray-200 rounded-lg" />
                  <div className="h-10 bg-gray-200 rounded-lg" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <i className="ri-search-2-line text-3xl mb-3 block" />
            <p>No APIs found for &ldquo;{search}&rdquo;</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-white rounded-2xl border border-gray-100 hover:border-brand-100 transition-all overflow-hidden group cursor-pointer"
                onClick={() => openProduct(p)}
              >
                <div className="h-1.5 w-full" style={{ backgroundColor: p.color }} />
                <div className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${p.color}18` }}>
                      <i className={`${p.icon} text-xl`} style={{ color: p.color }} />
                    </div>
                    <div className="text-right">
                      <span className="text-[11px] bg-gray-100 text-gray-500 px-2.5 py-1 rounded-full font-medium block mb-1">{p.category}</span>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${p.status === 'active' ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}>{p.status}</span>
                    </div>
                  </div>
                  <h3 className="font-bold text-gray-900 text-base mb-1 group-hover:text-brand-600 transition-colors">{p.name}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed mb-4 line-clamp-2">{p.description}</p>

                  <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                    <div className="bg-gray-50 rounded-lg py-2">
                      <div className="text-xs font-bold text-gray-900">{p.uptime}</div>
                      <div className="text-[10px] text-gray-400">Uptime</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg py-2">
                      <div className="text-xs font-bold text-gray-900">{p.endpoints}</div>
                      <div className="text-[10px] text-gray-400">Endpoints</div>
                    </div>
                    <div className="bg-gray-50 rounded-lg py-2">
                      <div className="text-xs font-bold text-gray-900">{(p.requests_per_month / 1000000).toFixed(1)}M</div>
                      <div className="text-[10px] text-gray-400">Req/mo</div>
                    </div>
                  </div>

                  <div className="border-t border-gray-100 pt-3 flex items-center justify-between">
                    <div>
                      <div className="text-[10px] text-gray-400">Lifetime from</div>
                      <div className="text-lg font-extrabold text-gray-900">${p.price_lifetime}</div>
                    </div>
                    <button
                      onClick={e => openProductModal(e, p)}
                      className="flex items-center gap-1.5 bg-brand-500 hover:bg-brand-600 text-white text-xs font-semibold px-4 py-2.5 rounded-lg cursor-pointer whitespace-nowrap transition-colors"
                    >
                      <i className="ri-shopping-cart-line" />Buy Now
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Footer sign-in nudge */}
      <div className="bg-white border-t border-gray-100 py-10 px-6 text-center">
        <p className="text-sm text-gray-500 mb-4">Already have an account? Access your purchased APIs from the dashboard.</p>
        <div className="flex items-center justify-center gap-3">
          <button onClick={() => navigate('/login?redirect=/marketplace')} className="px-6 py-2.5 border-2 border-sidebar text-sidebar text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap hover:bg-sidebar hover:text-white transition-all">Sign In</button>
          <button onClick={() => navigate('/register?redirect=/marketplace')} className="px-6 py-2.5 bg-brand-500 hover:bg-brand-600 text-white text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap transition-colors">Create Account</button>
        </div>
      </div>

      {/* Buy Modal */}
      <AnimatePresence>
        {selectedProduct && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
              <div className="h-1.5 w-full" style={{ backgroundColor: selectedProduct.color }} />
              <div className="p-6">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h3 className="font-bold text-gray-900">{selectedProduct.name}</h3>
                    <p className="text-xs text-gray-400">Choose your plan and payment method</p>
                  </div>
                  <button onClick={() => setSelectedProduct(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-gray-500" /></button>
                </div>

                {/* Plan Selection */}
                <div className="space-y-3 mb-5">
                  {(['price_1_2_year', 'price_3_5_year', 'price_lifetime'] as PlanKey[]).map(plan => (
                    <button key={plan} onClick={() => setSelectedPlan(plan)} className={`w-full text-left p-4 rounded-xl border-2 cursor-pointer transition-all ${selectedPlan === plan ? 'border-brand-500 bg-brand-50/30' : 'border-gray-100 hover:border-gray-200'}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-semibold text-gray-900">{planDuration[plan]}</div>
                          <div className="text-xs text-gray-400 mt-0.5">{plan === 'price_lifetime' ? 'Best value — never expires' : 'Instant access after payment'}</div>
                        </div>
                        <div className="text-xl font-extrabold text-gray-900">${selectedProduct[plan]}</div>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Payment Provider Selection */}
                <div className="mb-5">
                  <label className="text-xs text-gray-500 mb-2 block font-medium">Pay With</label>
                  <div className="flex gap-2 flex-wrap">
                    {nowpaymentsActive && (
                      <button 
                        onClick={() => setSelectedProvider('nowpayments')} 
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold border cursor-pointer whitespace-nowrap transition-all ${selectedProvider === 'nowpayments' ? 'bg-sidebar text-white border-sidebar' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}
                      >
                        <i className="ri-coin-line" />
                        Crypto (NOWPayments)
                      </button>
                    )}
                    {stripeActive && (
                      <button 
                        onClick={() => setSelectedProvider('stripe')} 
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold border cursor-pointer whitespace-nowrap transition-all ${selectedProvider === 'stripe' ? 'bg-sidebar text-white border-sidebar' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}
                      >
                        <i className="ri-bank-card-line" />
                        Card (Stripe)
                      </button>
                    )}
                    {!hasAnyProvider && (
                      <div className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                        <i className="ri-error-warning-line mr-1" />
                        No payment providers configured
                      </div>
                    )}
                  </div>
                </div>

                {/* Error Message */}
                {checkoutError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg space-y-1">
                    <p className="text-xs text-red-700 font-semibold flex items-center gap-1">
                      <i className="ri-error-warning-line shrink-0" />
                      Checkout failed
                    </p>
                    <p className="text-xs text-red-600 leading-relaxed break-words">{checkoutError}</p>
                    <p className="text-[11px] text-red-400 mt-1">
                      Check Admin → System Logs for full details, or try again in a moment.
                    </p>
                  </div>
                )}

                <button 
                  onClick={handleCheckout} 
                  disabled={checkoutLoading || !hasAnyProvider} 
                  className="w-full py-3.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white font-bold rounded-xl cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2"
                >
                  {checkoutLoading ? (
                    <><i className="ri-loader-4-line animate-spin" />Creating Checkout…</>
                  ) : (
                    <>
                      <i className="ri-secure-payment-line" />
                      Pay ${selectedProduct[selectedPlan]} with {selectedProvider === 'stripe' ? 'Card' : 'Crypto'}
                    </>
                  )}
                </button>
                <p className="text-[11px] text-gray-400 text-center mt-3">
                  {selectedProvider === 'stripe' 
                    ? 'Powered by Stripe · Secure card payment' 
                    : 'Powered by NOWPayments · Multiple cryptocurrencies supported'}
                </p>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}